import { defineStore } from 'pinia'
import type { TableHeader } from '@/models/general/table'

export const useUtilityStore = defineStore({
  id: 'utility',
  persist: true, // Enable persistence across reloads

  state: () => ({
    columnWidths: {} as { [key: string]: number }, // Store column widths by unique column key
    tableColumnSettings: {} as Record<string, TableHeader[]>,
    tableItemsPerPage: {} as Record<string, number>, // Store items-per-page by gridStorageId
  }),

  actions: {
    // Save or update the width of a column
    setColumnWidth(columnKey: string, width: number) {
      this.columnWidths[columnKey] = width
    },

    // Retrieve the saved width for a specific column
    getColumnWidth(columnKey: string) {
      return this.columnWidths[columnKey] || null
    },

    clearColumnWidth(columnKey: string) {
      delete this.columnWidths[columnKey]
    },

    // Clear Specific Table Column Widths
    clearSpecificTableColumnWidths(tableId: string) {
      // Iterate over the keys of the columnWidths object
      Object.keys(this.columnWidths).forEach(key => {
        // Check if the key starts with the tableId
        if (key.startsWith(tableId)) {
          // Delete the key from the columnWidths object
          delete this.columnWidths[key]
        }
      })
    },

    setTableColumnSettings(tableId: string, headers: TableHeader[]) {
      this.tableColumnSettings[tableId] = Array.isArray(headers)
        ? headers.map(header => ({ ...header }))
        : []
    },

    getTableColumnSettings(tableId: string) {
      const headers = this.tableColumnSettings[tableId]
      return Array.isArray(headers)
        ? headers.map(header => ({ ...header }))
        : []
    },

    clearTableColumnSettings(tableId: string) {
      delete this.tableColumnSettings[tableId]
    },

    setTableItemsPerPage(gridStorageId: string, value: number) {
      this.tableItemsPerPage[gridStorageId] = value
    },

    getTableItemsPerPage(gridStorageId: string): number | null {
      return this.tableItemsPerPage[gridStorageId] ?? null
    },

    clearTableItemsPerPage(gridStorageId: string) {
      delete this.tableItemsPerPage[gridStorageId]
    },
  }
})

// Define the type of the store
export type UtilityStore = ReturnType<typeof useUtilityStore>
